// Shared API client.
//
// Every call previously built its own axios request and hand-wrote the
// Authorization header, and error handling varied per call site. The backend now
// returns a uniform error body and uses distinct status codes that mean
// different things, so those decisions belong in one place:
//
//   401 — the session is gone (tokens now expire after 8h; they never used to).
//         Clear it and send the user to login.
//   403 — authenticated but not permitted. Must NOT log the user out.
//   429 — rate limited. Surface a wait time rather than a generic failure.

import axios from "axios";

export const BASE_URL = process.env.REACT_APP_DOMAIN;

/** Normalised error thrown to callers. */
export class ApiError extends Error {
  constructor({ status, code, message, details, requestId }) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isRateLimited() {
    return this.status === 429;
  }
}

/**
 * Pull a usable message out of whatever the server sent.
 *
 * The API returns `{ error: { code, message, details, requestId } }`, but older
 * deployments and proxies can still produce bare strings or `{error: "..."}`,
 * so all three shapes are handled.
 */
export function normalizeError(error) {
  if (!error.response) {
    return new ApiError({
      status: 0,
      code: "NETWORK_ERROR",
      message:
        "Could not reach the server. Check your connection and try again.",
    });
  }

  const { status, data } = error.response;
  let code = "UNKNOWN";
  let message = "Something went wrong. Please try again.";
  let details;
  let requestId;

  if (data && typeof data === "object" && data.error) {
    if (typeof data.error === "string") {
      message = data.error;
    } else {
      code = data.error.code || code;
      message = data.error.message || message;
      details = data.error.details;
      requestId = data.error.requestId;
    }
  } else if (typeof data === "string" && data.trim()) {
    message = data;
  }

  return new ApiError({ status, code, message, details, requestId });
}

export function clearSession() {
  localStorage.removeItem("token");
}

/** Set by App so the interceptor can redirect without importing the router. */
let onUnauthenticated = () => {
  clearSession();
  if (window.location.pathname !== "/login") {
    window.location.assign("/login?expired=1");
  }
};

export function setUnauthenticatedHandler(handler) {
  onUnauthenticated = handler;
}

const client = axios.create({
  baseURL: BASE_URL,
  headers: { "Content-Type": "application/json" },
});

client.interceptors.request.use((requestConfig) => {
  const token = localStorage.token;
  if (token && !requestConfig.headers.Authorization) {
    requestConfig.headers.Authorization = `Bearer ${token}`;
  }
  return requestConfig;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    const apiError = normalizeError(error);

    // Only 401 ends the session. A 403 means the account is valid but lacks
    // permission — logging out there would bounce a practitioner to the login
    // screen every time they touched an admin-only control.
    if (apiError.status === 401 && !error.config?.skipAuthRedirect) {
      onUnauthenticated(apiError);
    }

    return Promise.reject(apiError);
  }
);

// Session expiry also has to reach the call sites that still use axios directly.
// Tokens never used to expire, so none of them have a 401 path; rather than
// rewrite every one at once, the same handler is attached to the default axios
// instance. Those calls keep their existing error shape — only the logout
// behaviour is shared.
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
      onUnauthenticated(normalizeError(error));
    }
    return Promise.reject(error);
  }
);

export default client;
